import { useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import identity from 'lodash/identity';
import { connect } from 'formik';
import { differenceInCalendarDays } from 'date-fns';
import { rem } from 'polished';
import { DatePicker, Box } from 'roo-ui/components';

import Popover from 'components/Popover';
import { toDate, toCalendarDateFormat } from 'lib/utils/date';
import DateDisplay from './DateDisplay';

const defaultMinDate = new Date(0);
const calendarWidth = ['auto', rem('450px')];

const DatePickerInput = connect(({ formik, minDate, maxDate, title, onChange, ...field }) => {
  const input = useRef();
  const date = useMemo(() => toDate(field.value), [field.value]);
  const setDate = useCallback(
    (newDate) => {
      formik.setFieldValue(field.name, toCalendarDateFormat(newDate));
    },
    [field.name, formik],
  );
  const handleSelect = useCallback(
    ({ date }) => {
      setDate(date);
      input.current.focus();
      onChange(toCalendarDateFormat(date));
    },
    [setDate, onChange],
  );

  useEffect(() => {
    if (minDate && differenceInCalendarDays(date, minDate) < 0) setDate(minDate);
    else if (maxDate && differenceInCalendarDays(date, maxDate) > 0) setDate(maxDate);
  }, [date, minDate, maxDate, setDate]);

  return (
    <Popover
      title={title}
      renderReference={({ open, close, isOpen }) => <DateDisplay ref={input} onClick={open} onFocus={isOpen ? close : open} {...field} />}
      renderContent={() => (
        <Box py={8} px={[0, 4]} width={calendarWidth} data-testid="date-picker">
          <DatePicker {...field} minDate={minDate} maxDate={maxDate} date={date} selected={date} onDateSelected={handleSelect} />
        </Box>
      )}
    />
  );
});

DatePickerInput.defaultProps = {
  minDate: defaultMinDate,
  maxDate: undefined,
  value: PropTypes.string,
  onChange: identity,
};

DatePickerInput.propTypes = {
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default DatePickerInput;
